/**
 * @file IP Scoring Configuration
 * @description Defines thresholds and constants for the Investigation Points (IP) scoring system
 */

import { CommendationTier, type SpeedStreakThreshold } from '../types/scoring';

/**
 * Time-Efficiency Continuous Curve Parameters
 * Used for calculating time-efficiency points using continuous attenuation
 * Formula: maxPoints × 1 / (1 + (t / tHalf)^gamma)
 * Result is always rounded to nearest integer (no decimals)
 *
 * From BriefScoringSystemV2 spec - replaces threshold-based scoring
 */
export const TIME_EFFICIENCY_CURVE = {
  maxPoints: 120, // Maximum points for instant finds
  tHalf: 2.0, // Half-life: time (seconds) where points drop to 50%
  gamma: 2.0, // Steepness of the curve
};

/**
 * Speed streak thresholds
 * Defines the multipliers applied based on consecutive time efficiency bonuses
 * Values from BriefScoringSystemV2: [1.0, 1.6, 2.1, 2.6, 3.1] for streak lengths 1..5+
 */
export const SPEED_STREAK_THRESHOLDS: SpeedStreakThreshold[] = [
  { minConsecutiveBonuses: 1, multiplier: 1.0, label: 'Getting Started' },
  { minConsecutiveBonuses: 2, multiplier: 1.6, label: 'Building Momentum' },
  { minConsecutiveBonuses: 3, multiplier: 2.1, label: 'On Fire' },
  { minConsecutiveBonuses: 4, multiplier: 2.6, label: 'Unstoppable' },
  { minConsecutiveBonuses: 5, multiplier: 3.1, label: 'Legendary Speed' },
];

/**
 * Base points awarded for each evidence item found
 */
export const EVIDENCE_BASE_POINTS = 100;

/**
 * Accuracy Multiplier Continuous Curve Parameters
 * Used for calculating accuracy multiplier using continuous power attenuation
 *
 * Formula (two-step):
 * 1. adjusted_accuracy = (correct + α) / (correct + λ·wrong + α + β)
 * 2. multiplier = minAcc + (maxAcc − minAcc) × (adjusted_accuracy^γ)
 *
 * From BriefScoringSystemV2 spec - replaces threshold-based multipliers
 */
export const ACCURACY_MULTIPLIER_CURVE = {
  minAcc: 0.5, // Minimum multiplier (at 0% accuracy)
  maxAcc: 1.5, // Maximum multiplier (at 100% accuracy)
  gamma: 1.95, // Power curve steepness
  lambda: 1.0, // Weight for wrong clicks in denominator
  alpha: 1, // Smoothing factor for numerator
  beta: 1, // Smoothing factor for denominator
};

/**
 * Minimum required evidence to pass a level
 * This is a fallback value if not specified in the level config
 */
export const DEFAULT_REQUIRED_EVIDENCE = 3;

/**
 * LocalStorage key for storing personal best data
 */
export const PERSONAL_BEST_STORAGE_KEY = 'truth-trail-personal-bests';

/**
 * Pace Multiplier Configuration
 * Applied based on Total Completion Time (T) relative to ParTime/AceTime
 *
 * Formula:
 * p = clamp(0, 1, (ParTime - T) / (ParTime - AceTime))
 * paceMultiplier = paceMin + (paceMax - paceMin) * (p ^ gamma)
 */
export const PACE_MULTIPLIER_CONFIG = {
  enabled: true,
  paceMin: 0.95,
  paceMax: 1.1,
  gamma: 1.1,
};

/**
 * Default ParTime and AceTime values (in seconds)
 * These should be overridden per-case in case configuration
 */
export const DEFAULT_TIME_TARGETS = {
  parTime: 180,
  aceTime: 120,
};

/**
 * Case Pins Configuration
 * Calculated from Total Completion Time using ParTime/AceTime
 *
 * Formula:
 * p = clamp(0, 1, (ParTime - T) / (ParTime - AceTime))
 * pins = round(5 * p ^ gamma)
 */
export const CASE_PINS_CONFIG = {
  maxPins: 5,
  gamma: 1.1,
};

/**
 * Commendation Badge Tier Configuration
 * Based on Total Completion Time percentile (stub cutoffs until leaderboard)
 */
export const COMMENDATION_TIERS: Record<
  CommendationTier,
  { name: string; percentile: number; description: string }
> = {
  [CommendationTier.CHIEF_INSPECTOR]: {
    name: 'Chief Inspector',
    percentile: 1,
    description: 'Top 1%',
  },
  [CommendationTier.INSPECTOR]: {
    name: 'Inspector',
    percentile: 10,
    description: 'Top 10%',
  },
  [CommendationTier.DETECTIVE_GRADE_I]: {
    name: 'Detective Grade I',
    percentile: 20,
    description: 'Top 20%',
  },
  [CommendationTier.DETECTIVE_GRADE_II]: {
    name: 'Detective Grade II',
    percentile: 40,
    description: 'Top 40%',
  },
  [CommendationTier.DETECTIVE_GRADE_III]: {
    name: 'Detective Grade III',
    percentile: 60,
    description: 'Top 60%',
  },
  [CommendationTier.NONE]: {
    name: 'No Badge',
    percentile: 100,
    description: '',
  },
};

/**
 * Temporary stub time cutoffs for commendation badges (seconds)
 * Per-case values until real leaderboard data available
 */
export const DEFAULT_TIME_CUTOFFS = {
  t01: 50,
  t10: 70,
  t20: 90,
  t40: 110,
  t60: 130,
};
